import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  isAfter,
  isToday,
  isTomorrow,
} from "date-fns";
import { format, fromZonedTime, toZonedTime } from "date-fns-tz";

const PRESET_TIMES_LENGTH = 3;
const TARGET_HOURS = [0, 9, 17]; // Midnight, 9am, 5pm

export type PresetTimeOption = {
  key: number; // index
  value: string; // DateISOString in UTC time
  label: string; // ["Today"|"Tomorrow"] at [H]:00 ["AM"/"PM"]
};
export function getPresetTimeOptions(
  currentTime: Date,
  timezoneName: string
): PresetTimeOption[] {
  const currentZonedTime = toZonedTime(currentTime, timezoneName);
  const presetTimes: Date[] = [];
  let offsetDays = 0;

  while (presetTimes.length < PRESET_TIMES_LENGTH) {
    for (const hour of TARGET_HOURS) {
      const targetTime = addDays(currentZonedTime, offsetDays);
      targetTime.setHours(hour, 0, 0, 0);
      if (targetTime.getTime() > currentZonedTime.getTime()) {
        presetTimes.push(targetTime);
      }
      if (presetTimes.length >= PRESET_TIMES_LENGTH) {
        break;
      }
    }
    offsetDays += 1;
  }

  return presetTimes.map((presetTime, idx) => ({
    key: idx,
    value: fromZonedTime(presetTime, timezoneName).toISOString(),
    label: formatPresetTimeLabel(presetTime),
  }));
}

// ["Today"|"Tomorrow"] at [H]:00 ["AM"/"PM"]
export function formatPresetTimeLabel(date: Date): string {
  if (isToday(date)) {
    return `Today at ${format(date, "h:mm a")}`;
  } else if (isTomorrow(date)) {
    return `Tomorrow at ${format(date, "h:mm a")}`;
  } else {
    return format(date, "MMMM do 'at' h:mm a");
  }
}

export function getDayWithSuffix(date: Date) {
  const day = date.getDate();
  if (day > 3 && day < 21) return `${day}th`;
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
}

export function formatTimeWithTimeZone(
  time: Date,
  timeZone: string,
  adjustForDST: boolean
) {
  const clock = format(time, "h:mmaaa", { timeZone });

  // use generic zone (e.g. ET) when adjustForDST is true,
  // otherwise use a DST-specific abbreviation (e.g. EDT)
  const z =
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: adjustForDST ? "shortGeneric" : "short",
    })
      .formatToParts(fromZonedTime(time, timeZone))
      .find((p) => p.type === "timeZoneName")?.value ?? "";
  return `${clock} ${z}`;
}

export enum RepeatMode {
  Daily = "daily",
  Weekly = "weekly",
  Monthly = "monthly",
  Yearly = "yearly",
}

export const repeatModes = [
  RepeatMode.Daily,
  RepeatMode.Weekly,
  RepeatMode.Monthly,
  RepeatMode.Yearly,
];

type RepeatModeOption = {
  label: string;
  formatFormDescription: (
    time: Date | undefined,
    timeZone: string,
    adjustForDST: boolean | undefined
  ) => string;
  formatDescription: (
    time: Date,
    timeZone: string,
    adjustForDST: boolean
  ) => string;
};

export const REPEAT_MODE_OPTIONS: {
  [key in RepeatMode]: RepeatModeOption;
} = {
  [RepeatMode.Daily]: {
    label: "Daily",
    formatFormDescription: (
      time: Date | undefined,
      timeZone: string,
      adjustForDST: boolean | undefined = false
    ) =>
      `Every day ${
        time ? `at ${formatTimeWithTimeZone(time, timeZone, adjustForDST)}` : ""
      }`,
    formatDescription: (time: Date, timeZone: string, adjustForDST: boolean) =>
      `daily at ${formatTimeWithTimeZone(time, timeZone, adjustForDST)}`,
  },
  [RepeatMode.Weekly]: {
    label: "Weekly",
    formatFormDescription: (
      time: Date | undefined,
      timeZone: string,
      adjustForDST: boolean | undefined = false
    ) =>
      `Every week ${
        time
          ? `on ${format(time, "EEEE", {
              timeZone,
            })} at ${formatTimeWithTimeZone(time, timeZone, adjustForDST)}`
          : ""
      }`,
    formatDescription: (time: Date, timeZone: string, adjustForDST: boolean) =>
      `weekly on ${format(time, "EEEE", {
        timeZone,
      })} at ${formatTimeWithTimeZone(time, timeZone, adjustForDST)}`,
  },
  [RepeatMode.Monthly]: {
    label: "Monthly",
    formatFormDescription: (
      time: Date | undefined,
      timeZone: string,
      adjustForDST: boolean | undefined = false
    ) =>
      `Every month ${
        time
          ? `on the ${getDayWithSuffix(time)} at ${formatTimeWithTimeZone(
              time,
              timeZone,
              adjustForDST
            )}`
          : ""
      }`,
    formatDescription: (time: Date, timeZone: string, adjustForDST: boolean) =>
      `monthly on the ${getDayWithSuffix(time)} at ${formatTimeWithTimeZone(
        time,
        timeZone,
        adjustForDST
      )}`,
  },
  [RepeatMode.Yearly]: {
    label: "Yearly",
    formatFormDescription: (
      time: Date | undefined,
      timeZone: string,
      adjustForDST: boolean | undefined = false
    ) =>
      `Every year ${
        time
          ? `on ${format(time, "MMMM d", {
              timeZone,
            })} at ${formatTimeWithTimeZone(time, timeZone, adjustForDST)}`
          : ""
      } `,
    formatDescription: (time: Date, timeZone: string, adjustForDST: boolean) =>
      `yearly on ${format(time, "MMMM d", {
        timeZone,
      })} at ${formatTimeWithTimeZone(time, timeZone, adjustForDST)}`,
  },
};

const REPEAT_MODE_UNITS = {
  [RepeatMode.Daily]: { add: addDays, diff: differenceInDays },
  [RepeatMode.Weekly]: { add: addWeeks, diff: differenceInWeeks },
  [RepeatMode.Monthly]: { add: addMonths, diff: differenceInMonths },
  [RepeatMode.Yearly]: { add: addYears, diff: differenceInYears },
};

export function isFutureExportTime(exportTimeStr: string) {
  const exportTimeUtc = new Date(exportTimeStr);
  const nowTimeUtc = new Date();
  return isAfter(exportTimeUtc, nowTimeUtc);
}

export function getNextRunTime(
  repeatMode: RepeatMode | null,
  exportTimeStr: string
) {
  const exportTimeUtc = new Date(exportTimeStr);
  const nowTimeUtc = new Date();
  if (isAfter(exportTimeUtc, nowTimeUtc) || !repeatMode) {
    return exportTimeUtc;
  }
  const unit = REPEAT_MODE_UNITS[repeatMode];
  const intervals = Math.max(1, unit.diff(nowTimeUtc, exportTimeUtc) + 1);
  const nextTimeUtc = unit.add(exportTimeUtc, intervals);
  return nextTimeUtc;
}

export function isTimeZoneUtc(timeZone: string | undefined | null): boolean {
  if (timeZone == null || timeZone === "") {
    return true;
  }
  return timeZone.toUpperCase() === "UTC";
}
